import { ACTIVE_STATUSES } from '../types/car.js';
import { catToBucket } from '../types/cost.js';
import { emptyBuckets } from './costs.js';
import { AFSCHRIJF_MAANDEN, afschrijving } from './depreciation.js';
import { isEigenBezit } from './lease.js';
import { vasteLastenInMaand } from './tarief.js';
import { MONTH_LABELS } from './dates.js';
export const TCO_POSTEN = [
    { key: 'afschrijving', label: 'Afschrijving', color: '#2A6085', vast: true },
    { key: 'lease', label: 'Lease/leen', color: '#1F4C6B', vast: true },
    { key: 'verzekering', label: 'Verzekering', color: '#6E9AA5', vast: true },
    { key: 'wegenbelasting', label: 'Wegenbelasting', color: '#E0A943', vast: true },
    { key: 'vastOverig', label: 'Overige vaste lasten', color: '#8C8272', vast: true },
    { key: 'onderhoud', label: 'Onderhoud', color: '#B0543B', vast: false },
    { key: 'variabel', label: 'Variabel', color: '#BCD4DA', vast: false },
];
/**
 * Which layer a rate's `soort` lands in. Keyword-based like `catToBucket`, and
 * for the same reason: `soort` is free text so a new kind of fixed cost needs no
 * migration, and anything unrecognised has to end up somewhere visible instead
 * of dropping silently out of the stack — hence `vastOverig`.
 */
export function soortNaarPost(soort) {
    const s = soort.toLowerCase();
    if (/mrb|wegenbelasting|motorrijtuig/.test(s))
        return 'wegenbelasting';
    if (/verzekering|premie|polis|casco/.test(s))
        return 'verzekering';
    if (/lease|leen|huur/.test(s))
        return 'lease';
    return 'vastOverig';
}
/**
 * Depreciation per month as a cost *of ours*, or 0 when there is none.
 *
 * The arithmetic lives in `depreciation.ts` and is shared with `restwaarde`, so
 * the card and the chart cannot drift apart. What this adds is the ownership
 * question: with lease or a loaner the depreciation is the lessor's, and for us it
 * sits inside the monthly instalment.
 */
export function afschrijvingPerMaand(car) {
    if (!isEigenBezit(car))
        return 0; // niet ons bezit
    return afschrijving(car);
}
/** `YYYY-MM` → months since epoch, so month maths stays integer arithmetic. */
function maandIndex(maand) {
    const [j, m] = maand.split('-');
    return Number(j) * 12 + (Number(m) - 1);
}
function indexNaarMaand(idx) {
    const j = Math.floor(idx / 12);
    const m = (idx % 12) + 1;
    return `${j}-${String(m).padStart(2, '0')}`;
}
function maandVanDatum(iso) {
    return iso.slice(0, 7);
}
/**
 * The month the series starts: the later of the car's first cost line and its
 * purchase month.
 *
 * Not simply the purchase date. The invoices only go back to mid-2025, so a car
 * bought in 2022 would otherwise get bars for 2022 to 2024 built purely from
 * today's fixed rates, with no measured cost in them at all, a number that
 * looks like history but is extrapolation. Taking the later of the two also gets
 * a car bought *inside* the data window right: there the purchase month is the
 * honest start.
 */
function startMaand(car, regels) {
    const maanden = regels
        .map((r) => r.maand)
        .filter(Boolean)
        .sort();
    const eersteData = maanden[0] ?? null;
    const koop = car.aanschafdatum ? maandVanDatum(car.aanschafdatum) : null;
    if (eersteData && koop)
        return eersteData > koop ? eersteData : koop;
    return eersteData ?? koop;
}
/**
 * The month the car stopped costing money, or null while it is still running.
 *
 * An archived car (sold, written off, handed back) stops accruing depreciation
 * and road tax, but there is no disposal date on `Car`, so its last cost line is
 * the best available bound. Without it a written-off car keeps depreciating: over
 * ten months that was 83% of ZS358T's entire reported TCO.
 */
function eindMaand(car, regels) {
    if (ACTIVE_STATUSES.includes(car.status))
        return null;
    const maanden = regels
        .map((r) => r.maand)
        .filter(Boolean)
        .sort();
    return maanden[maanden.length - 1] ?? null;
}
/**
 * The months a period spans, bounded by the car's own lifetime and the present,
 * so a yearly bar for the year of purchase counts only the months actually owned.
 */
function actieveMaandenIn(vanIdx, totIdx, startIdx, eindIdx, nuIdx) {
    const van = startIdx == null ? vanIdx : Math.max(vanIdx, startIdx);
    const tot = Math.min(totIdx, eindIdx ?? nuIdx, nuIdx);
    return Math.max(0, tot - van + 1);
}
/**
 * Months of a period inside the 60-month window, the car's lifetime *and* the
 * series window: a bar must not charge twelve months of depreciation while
 * saying it only covers six active months.
 */
function afschrijfMaandenIn(car, vanIdx, totIdx, startIdx, eindIdx, nuIdx) {
    if (!car.aanschafdatum)
        return 0;
    const koopIdx = maandIndex(maandVanDatum(car.aanschafdatum));
    const afschrijfEind = koopIdx + AFSCHRIJF_MAANDEN - 1;
    const van = Math.max(vanIdx, koopIdx, startIdx ?? koopIdx);
    const tot = Math.min(totIdx, afschrijfEind, eindIdx ?? nuIdx, nuIdx);
    return Math.max(0, tot - van + 1);
}
/**
 * The years this car has a story for, oldest first: the options for the period
 * selector. Derived from the data, so it grows with the car instead of relying on
 * a fixed window.
 */
export function tcoJaren(car, regels, now = new Date()) {
    const start = startMaand(car, regels);
    const eind = eindMaand(car, regels);
    const eersteJaar = start ? Math.floor(maandIndex(start) / 12) : now.getFullYear();
    const laatsteJaar = eind ? Math.floor(maandIndex(eind) / 12) : now.getFullYear();
    const jaren = [];
    for (let j = eersteJaar; j <= Math.max(eersteJaar, laatsteJaar); j++)
        jaren.push(j);
    return jaren;
}
/** The periods to show, oldest first. */
function periodes(soort, jaar, now, startIdx, eindIdx) {
    const nuIdx = now.getFullYear() * 12 + now.getMonth();
    const laatsteIdx = Math.min(eindIdx ?? nuIdx, nuIdx);
    if (soort === 'jaar') {
        const startJaar = startIdx != null ? Math.floor(startIdx / 12) : now.getFullYear();
        const eindJaar = Math.floor(laatsteIdx / 12);
        const jaren = [];
        for (let j = startJaar; j <= Math.max(startJaar, eindJaar); j++) {
            jaren.push({ key: String(j), label: String(j), vanIdx: j * 12, totIdx: j * 12 + 11 });
        }
        return jaren;
    }
    // Met een jaar: januari t/m december van dat jaar. Zonder jaar: elke maand van
    // het hele venster. In beide gevallen hele kalenderjaren aan het eind, ook als
    // die maanden nog nul zijn: een vaste as leest rustiger, houdt de breedte gelijk
    // als er een maand bijkomt, en vult de as op breedbeeld in plaats van een paar
    // staven met gaten ertussen.
    const eerste = jaar != null ? jaar * 12 : (startIdx ?? laatsteIdx);
    const laatste = jaar != null ? jaar * 12 + 11 : Math.floor(laatsteIdx / 12) * 12 + 11;
    const maanden = [];
    for (let i = eerste; i <= laatste; i++) {
        const maand = MONTH_LABELS[i % 12];
        // Loopt de reeks over jaargrenzen, dan staat het jaartal er alleen bij waar het
        // verandert; anders staat 'jul 2026' twaalf keer naast elkaar.
        const jaarErbij = jaar == null && (i % 12 === 0 || i === eerste);
        maanden.push({
            key: indexNaarMaand(i),
            label: jaarErbij ? `${maand} ${Math.floor(i / 12)}` : maand,
            vanIdx: i,
            totIdx: i,
        });
    }
    return maanden;
}
function leegPosten() {
    return {
        afschrijving: 0,
        lease: 0,
        verzekering: 0,
        wegenbelasting: 0,
        vastOverig: 0,
        onderhoud: 0,
        variabel: 0,
    };
}
/**
 * One car priced month by month over `van` … `tot` (inclusive `YYYY-MM` keys).
 *
 * The window is what to *report on*; the car's own lifetime is what decides
 * whether a month is `actief`. Those are two different things, and separating
 * them is what lets the fleet chart put every car on one shared axis: a car
 * bought halfway simply has inactive months before it, contributing nothing,
 * instead of needing an axis of its own.
 *
 * Every month in the window comes back, including the empty ones, so a caller
 * summing them never has to guess whether a missing key means zero or means
 * out of range.
 */
export function tcoMaandenVoorAuto({ car, regels, tarieven = [], van, tot, now = new Date(), }) {
    const nuIdx = now.getFullYear() * 12 + now.getMonth();
    const start = startMaand(car, regels);
    const startIdx = start ? maandIndex(start) : null;
    const eind = eindMaand(car, regels);
    const eindIdx = eind ? maandIndex(eind) : null;
    const eigen = isEigenBezit(car);
    const afschrijfMnd = afschrijvingPerMaand(car);
    // Bij eigen bezit tellen alle vaste lasten behalve een lease- of leentermijn;
    // bij lease en leen precies andersom, want wegenbelasting, verzekering en
    // onderhoud zitten dan ín die termijn. Dezelfde regel als voorheen, alleen
    // gesteld in soorten in plaats van in velden op Car — een MRB-tarief dat per
    // ongeluk op een leaseauto staat telt dus nog steeds niet mee.
    const teltMee = (s) => (soortNaarPost(s) === 'lease') !== eigen;
    // Gemeten kosten per maand, vooraf gegroepeerd zodat elke maand één lookup is.
    const gemeten = new Map();
    for (const r of regels) {
        if (!r.maand)
            continue;
        const b = gemeten.get(r.maand) ?? emptyBuckets();
        b[catToBucket(r.categorie)] += Number(r.bedragExcl) || 0;
        gemeten.set(r.maand, b);
    }
    const uit = new Map();
    for (let i = maandIndex(van); i <= maandIndex(tot); i++) {
        const maand = indexNaarMaand(i);
        const posten = leegPosten();
        const variabelPerBucket = emptyBuckets();
        // Gemeten kosten worden niet begrensd door de levensduur: wat geboekt is, is
        // geboekt. Alleen niet vooruit in de tijd.
        if (i <= nuIdx) {
            const b = gemeten.get(maand);
            if (b) {
                for (const k of Object.keys(b)) {
                    if (k === 'onderhoud')
                        posten.onderhoud += b[k];
                    else {
                        variabelPerBucket[k] += b[k];
                        posten.variabel += b[k];
                    }
                }
            }
        }
        // Vaste lasten alleen over de maanden die de auto echt liep, elk tegen het
        // tarief van díe maand. Per maand rekenen in plaats van één bedrag maal het
        // aantal maanden is het hele punt van de tariefhistorie: een staaf van vóór
        // een verhoging blijft op het oude bedrag staan.
        const actief = actieveMaandenIn(i, i, startIdx, eindIdx, nuIdx) > 0;
        if (actief) {
            const perSoort = vasteLastenInMaand(tarieven, maand);
            for (const s of Object.keys(perSoort)) {
                if (!teltMee(s))
                    continue;
                posten[soortNaarPost(s)] += perSoort[s];
            }
        }
        if (afschrijfMaandenIn(car, i, i, startIdx, eindIdx, nuIdx) > 0) {
            posten.afschrijving += afschrijfMnd;
        }
        uit.set(maand, { posten, variabelPerBucket, actief });
    }
    return uit;
}
/**
 * Build the TCO series for one car: the months from `tcoMaandenVoorAuto` rolled
 * up into the bars the period selector asked for. Everything is derived from the
 * cost lines plus the car's own fields, so this stays a pure function.
 */
export function buildTco({ car, regels, tarieven = [], soort, jaar, now = new Date(), }) {
    const start = startMaand(car, regels);
    const startIdx = start ? maandIndex(start) : null;
    const eind = eindMaand(car, regels);
    const eindIdx = eind ? maandIndex(eind) : null;
    const vensters = periodes(soort, jaar, now, startIdx, eindIdx);
    if (!vensters.length)
        return [];
    const maanden = tcoMaandenVoorAuto({
        car,
        regels,
        tarieven,
        van: indexNaarMaand(vensters[0].vanIdx),
        tot: indexNaarMaand(vensters[vensters.length - 1].totIdx),
        now,
    });
    return vensters.map(({ key, label, vanIdx, totIdx }) => {
        const posten = leegPosten();
        const variabelPerBucket = emptyBuckets();
        let actieveMaanden = 0;
        for (let i = vanIdx; i <= totIdx; i++) {
            const m = maanden.get(indexNaarMaand(i));
            if (!m)
                continue;
            if (m.actief)
                actieveMaanden++;
            for (const k of Object.keys(posten))
                posten[k] += m.posten[k];
            for (const k of Object.keys(variabelPerBucket)) {
                variabelPerBucket[k] += m.variabelPerBucket[k];
            }
        }
        const totaal = Object.keys(posten).reduce((s, k) => s + posten[k], 0);
        return { key, label, posten, variabelPerBucket, totaal, actieveMaanden };
    });
}
/** Sum of one post over the series, for the legend and the totals row. */
export function totaalPerPost(reeks) {
    const t = Object.fromEntries(TCO_POSTEN.map((p) => [p.key, 0]));
    for (const p of reeks)
        for (const k of Object.keys(t))
            t[k] += p.posten[k];
    return t;
}
/** Grand total over the series. */
export function totaalTco(reeks) {
    return reeks.reduce((s, p) => s + p.totaal, 0);
}
//# sourceMappingURL=tco.js.map